import React from 'react';
import { useIntl } from 'react-intl';
import { matchPath, useLocation } from 'react-router';
import { NavLink } from 'react-router-dom';

import { Badge } from '@bolteu/clay-react';

import { $tid } from '@provider-portal/common/utils/testIdConfig';
import { ClayScope } from '@provider-portal/design-common/clay/ClayScope';
import { List } from '@provider-portal/design-common/kalep/components/List/List';
import { ListItem } from '@provider-portal/design-common/kalep/components/ListItem/ListItem';
import { ListItemButton } from '@provider-portal/design-common/kalep/components/ListItemButton/ListItemButton';
import { ListItemText } from '@provider-portal/design-common/kalep/components/ListItemText/ListItemText';

import { ConfigMenuItem } from 'root/navigation/navTypes';
import { MenuContext } from 'root/navigation/navStructureUtils';
import { useVisitedNewBadges } from 'root/navigation/useVisitedNewBadges';

import { $tk } from 'utils/appTranslationKey';

interface SideMenuItemProps {
  text: string;
  index: string;
  isSelected: boolean;
  disabled: boolean;
  href?: string;
  badgeLabel?: string;
  onMenuItemClick?: (menuItem: NavBarMenuItem) => void;
}

const navigationItemIdsToTestIdsMapping: Record<string, string> = {
  dashboard: $tid.components.SideBar.Analytics.DashBoard,
  merchantWeeklyReport: $tid.components.SideBar.Analytics.WeeklyReport,
  itemReport: $tid.components.SideBar.Analytics.DetailedReports,
  invoicesAndSalesReports: $tid.components.SideBar.Analytics.InvoicesAndSales,
  orderHistory: $tid.components.SideBar.Analytics.OrderHistory,
  'org-summary': $tid.components.SideBar.MenuEditor.OrgSummary,
  'my-menu': $tid.components.SideBar.MenuEditor.MyMenu,
  items: $tid.components.SideBar.MenuEditor.Items,
  modifiers: $tid.components.SideBar.MenuEditor.Modifiers,
  timetables: $tid.components.SideBar.MenuEditor.Timetables,
};

const getTestIdForItemId = (itemId: string): string => {
  if (navigationItemIdsToTestIdsMapping[itemId]) {
    return navigationItemIdsToTestIdsMapping[itemId];
  }

  return '';
};

export const NavMenuItem = ({
  text,
  index,
  isSelected,
  disabled,
  href,
  badgeLabel,
  onMenuItemClick,
}: SideMenuItemProps) => {
  const handleClick = React.useCallback(() => {
    onMenuItemClick?.({
      id: index,
      titleIntlId: text,
      href,
      disabled,
    });
  }, [disabled, href, index, onMenuItemClick, text]);

  return (
    <ListItem key={`${text}-${index}`} disablePadding sx={{ mb: 1 }}>
      <ListItemButton
        selected={isSelected}
        aria-label={text}
        disabled={disabled}
        component={NavLink}
        {...{ to: href }}
        disableRipple
        data-testid={getTestIdForItemId(index)}
        onClick={handleClick}
        sx={{ gap: '8px' }}
      >
        <ListItemText primary={text} sx={{ flex: '0 1 auto' }} />
        {badgeLabel && (
          <ClayScope>
            <Badge type="pill" size="sm" variant="action-secondary">
              <Badge.Content>{badgeLabel}</Badge.Content>
            </Badge>
          </ClayScope>
        )}
      </ListItemButton>
    </ListItem>
  );
};
export interface NavBarMenuItem extends ConfigMenuItem {
  disabled?: boolean;
}
export interface NavBarMenuProps {
  menuItems?: NavBarMenuItem[];
  onMenuItemClick?: (menuItem: NavBarMenuItem) => void;
}

export function NavBarMenu({ menuItems, onMenuItemClick }: NavBarMenuProps) {
  const location = useLocation();
  const intl = useIntl();
  const menuContext = React.useContext(MenuContext);
  const visitedBadgeHrefs = useVisitedNewBadges({
    menuItems: menuItems ?? [],
    pathname: location.pathname,
  });

  const menuItemsLayout = React.useMemo(() => {
    return menuItems.map((item) => {
      if (item.hidden) {
        return null;
      }
      const isPathMatch = matchPath(location.pathname, {
        path: item.href,
      });

      const shouldOverrideActiveState = menuContext?.activeMenuEntryIdOverride === item.id;
      const isSelected = isPathMatch || shouldOverrideActiveState;
      const shouldShowNewBadge =
        item.showNewBadge && !isPathMatch && !visitedBadgeHrefs.includes(item.href);

      return (
        <NavMenuItem
          key={item.id}
          text={intl.formatMessage({ id: item.titleIntlId })}
          index={item.id}
          isSelected={!!isSelected}
          disabled={item.disabled ?? false}
          href={item.href}
          badgeLabel={
            shouldShowNewBadge
              ? intl.formatMessage({ id: $tk.components.NavBar.newBadge })
              : undefined
          }
          onMenuItemClick={onMenuItemClick}
        />
      );
    });
  }, [
    menuItems,
    location.pathname,
    menuContext?.activeMenuEntryIdOverride,
    visitedBadgeHrefs,
    intl,
    onMenuItemClick,
  ]);

  return <List>{menuItemsLayout}</List>;
}
